import React, { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useSelector } from 'react-redux';
import Button from '../../../../../components/ComponentsV1/Button';
import InputV1 from '../../../../../components/ComponentsV1/InputV1';
import { TableV1 } from '../../../../../components/ComponentsV1/Table/Table';
import { Input } from '../../../../../UiKit';
import { useClient } from '../../../../../api/api-client';
import { IVA_POLICIES_ENDPOINTS } from '../constants';
import { toQuery, readErrorMessage } from '../helpers';
import { useComplaintTypes } from '../useComplaintTypes';
import IvaSkeleton from './IvaSkeleton';
import ConfirmModal from './ConfirmModal';

const MAPPING_COLUMNS = [
  { key: 'category_id', name: 'Category Id' },
  { key: 'group_id', name: 'Group Id' },
  { key: 'group_name', name: 'Group Name' },
  { key: 'actions', name: 'Actions' },
];

const MappingsSection = () => {
  const client = useClient();
  const updatedBy = useSelector(
    (state) => state.authState?.userDetails?.crm_user_id,
  );
  const [complaintType, setComplaintType] = useState('');
  const [mappings, setMappings] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({
    categoryId: '',
    groupId: '',
    groupName: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [formKey, setFormKey] = useState(0);

  const { options: complaintTypeOptions, isLoading: metadataLoading } =
    useComplaintTypes();

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleComplaintTypeChange = useCallback((opt) => {
    setComplaintType(opt?.value || '');
    setMappings(null);
  }, []);

  const fetchMappings = useCallback(async () => {
    if (!complaintType.trim()) {
      toast.error('Complaint type is required');
      return;
    }
    setIsLoading(true);
    try {
      const response = await client(
        `${IVA_POLICIES_ENDPOINTS.MAPPINGS_FETCH}${toQuery({
          complaint_type: complaintType,
        })}`,
      );
      setMappings(Array.isArray(response) ? response : response?.data || []);
    } catch (error) {
      setMappings(null);
      toast.error(readErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [client, complaintType]);

  const handleSave = useCallback(async () => {
    if (!complaintType.trim()) {
      toast.error('Complaint type is required');
      return;
    }
    if (!form.categoryId.trim() || !form.groupId.trim()) {
      toast.error('Category id and group id are required');
      return;
    }
    setIsSaving(true);
    try {
      await client(IVA_POLICIES_ENDPOINTS.MAPPINGS_CREATE_OR_UPDATE, {
        data: {
          complaint_type: complaintType.trim(),
          category_id: form.categoryId.trim(),
          group_id: form.groupId.trim(),
          group_name: form.groupName.trim(),
          updated_by: String(updatedBy || ''),
        },
      });
      toast.success('Mapping saved successfully');
      setForm({ categoryId: '', groupId: '', groupName: '' });
      setFormKey((prev) => prev + 1);
      fetchMappings();
    } catch (error) {
      toast.error(readErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  }, [client, complaintType, form, fetchMappings]);

  const handleDelete = useCallback(
    async (categoryId) => {
      setConfirmDeleteId(null);
      setDeletingId(categoryId);
      try {
        await client(
          `${IVA_POLICIES_ENDPOINTS.MAPPINGS_DELETE}${toQuery({
            complaint_type: complaintType,
            category_id: categoryId,
          })}`,
          { method: 'DELETE' },
        );
        toast.success('Mapping deleted successfully');
        fetchMappings();
      } catch (error) {
        toast.error(readErrorMessage(error));
      } finally {
        setDeletingId(null);
      }
    },
    [client, complaintType, fetchMappings],
  );

  const rows = Array.isArray(mappings)
    ? mappings.map((mapping) => ({
        category_id: { title: mapping.category_id },
        group_id: { title: mapping.group_id },
        group_name: { title: mapping.group_name || '—' },
        actions: {
          title: (
            <Button
              title='Delete'
              type='outline'
              onClick={() => setConfirmDeleteId(mapping.category_id)}
              disabled={deletingId === mapping.category_id}
              loading={deletingId === mapping.category_id}
            />
          ),
        },
      }))
    : [];

  return (
    <div className='tw-flex tw-flex-col tw-gap-6'>
      <div className='tw-bg-white tw-p-8 tw-rounded-2xl tw-border tw-border-solid tw-border-grey-200 tw-flex tw-flex-col tw-gap-4'>
        <label className='tw-text-[1.4rem] tw-font-medium tw-text-grey-800'>
          Complaint Type<span className='tw-text-red-500'>*</span>
        </label>
        <div className='tw-flex tw-gap-4 tw-items-center'>
          <div className='tw-flex-1'>
            <InputV1
              loading={metadataLoading}
              options={complaintTypeOptions}
              placeholder='Choose complaint type'
              multiple={false}
              isSearchable
              closeOnSelect
              onSelect={handleComplaintTypeChange}
              value={complaintType}
              style={{ width: '100%', display: 'block' }}
            />
          </div>
          <Button
            title='Fetch Mappings'
            onClick={fetchMappings}
            disabled={isLoading}
            loading={isLoading}
          />
        </div>
      </div>

      <div className='tw-bg-white tw-p-8 tw-rounded-2xl tw-border tw-border-solid tw-border-grey-200 tw-flex tw-flex-col tw-gap-4'>
        <div className='tw-text-[1.6rem] tw-font-medium tw-text-grey-900'>
          Create or Update Mapping
        </div>
        <div className='tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-6'>
          <Input
            key={`categoryId-${formKey}`}
            placeholder='Category Id'
            value={form.categoryId}
            showMaxLimit={false}
            inputStyleProps={{ width: '100%' }}
            onChange={(value) => setField('categoryId', value)}
          />
          <Input
            key={`groupId-${formKey}`}
            placeholder='Group Id'
            value={form.groupId}
            showMaxLimit={false}
            inputStyleProps={{ width: '100%' }}
            onChange={(value) => setField('groupId', value)}
          />
          <Input
            key={`groupName-${formKey}`}
            placeholder='Group Name'
            value={form.groupName}
            showMaxLimit={false}
            inputStyleProps={{ width: '100%' }}
            onChange={(value) => setField('groupName', value)}
          />
        </div>
        <div>
          <Button
            title='Save Mapping'
            onClick={handleSave}
            disabled={isSaving}
            loading={isSaving}
          />
        </div>
      </div>

      {isLoading && <IvaSkeleton />}

      {!isLoading && Array.isArray(mappings) && mappings.length === 0 && (
        <div className='tw-bg-white tw-p-8 tw-rounded-2xl tw-border tw-border-solid tw-border-grey-200 tw-text-[1.4rem] tw-text-grey-500'>
          No mappings found for this complaint type.
        </div>
      )}

      {!isLoading && Array.isArray(mappings) && mappings.length > 0 && (
        <div className='tw-bg-white tw-p-8 tw-rounded-2xl tw-border tw-border-solid tw-border-grey-200'>
          <TableV1 columns={MAPPING_COLUMNS} rows={rows} />
        </div>
      )}

      {confirmDeleteId !== null && (
        <ConfirmModal
          title='Delete Mapping'
          message={`Are you sure you want to delete the mapping for category ${confirmDeleteId}? This action cannot be undone.`}
          onConfirm={() => handleDelete(confirmDeleteId)}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
};

export default MappingsSection;
